import { useCallback, useMemo } from "react";
import { IATA } from "@vmosolutions/oc-interfaces";
import { FormikSelectField } from "src/components/FormikComponents/FormikWrappers";
import { ISelectFieldProps } from "src/components/FormikComponents/SelectField";
// API Calls
import { formatFunction, parseFunction } from "./helpers";

import { useSelector } from "react-redux";
import { IRootState } from "src/redux/reducers/CombinedReducers";

interface IATASelectorProps extends Omit<ISelectFieldProps, "options"> {
  mosaicPane: HTMLElement;
  valueKey?: string; // valueKey sent back as value - use entire object otherwise.
  showInactive?: boolean;
}

/** Sort types list Alphabetically */
const sortTypesByDescription = (a: IATA, b: IATA): number => {
  return a.ataNumber.toLowerCase().localeCompare(b.ataNumber.toLowerCase());
};

/**
 * Sepcialized wrapper for "built-in" oc ATA select field.
 * @param props -
 */
export const ATASelector = (props: IATASelectorProps): JSX.Element => {
  const { mosaicPane, label, name, onChange, valueKey, isMulti, defaultValue, ...selectProps } = props;

  const structures = useSelector((state: IRootState) => state.structures);
  const ataTypes = useMemo(() => {
    return structures?.customerStructure?.ATAs ?? [];
  }, [structures?.customerStructure?.ATAs]);

  const options = useMemo(() => ataTypes.sort(sortTypesByDescription), [ataTypes]);

  // Setup Format And Parse functions (used if valueKey is passed)
  const formatValue = useCallback(
    (value: string) => formatFunction(value, ataTypes, valueKey, isMulti),
    [ataTypes, valueKey, isMulti]
  );
  const parseOption = useCallback((option: string) => parseFunction(option, valueKey, isMulti), [valueKey, isMulti]);

  return (
    <FormikSelectField
      name={name}
      label={label ?? "ATA Selector"}
      isMulti={isMulti}
      options={options}
      getOptionLabel={(type: IATA) => `${type.ataNumber} - ${type.ataChapterName}`}
      getOptionValue={(type: IATA) => type._id}
      format={(value) => (valueKey ? formatValue(value) : value)}
      parse={(option) => (valueKey ? parseOption(option) : option)}
      onChange={(value: any) => !!onChange && onChange(value)}
      menuPortalTarget={mosaicPane}
      menuPosition={mosaicPane ? "fixed" : undefined}
      isSearchable={true}
      openMenuOnFocus={false}
      isClearable={true}
      placeholder={`Select ATA Type${isMulti && "s"}`}
      defaultValue={defaultValue}
      {...selectProps} // override with any props passed in such as isMulti, format, parse.
    />
  );
};
